import { API } from "aws-amplify";
import { GRAPHQL_AUTH_MODE } from "@aws-amplify/api";
import { useDispatch } from "react-redux";
import { setListing, setSelected } from "../store/ducks/userConcepts";
import { HeadCell } from "../models/dataTable";
import useApp from "./useApp";
import {
  GetUserConceptsQueryVariables,
  CreateUserConceptsInput,
  UpdateUserConceptsMutationVariables,
  UpdateUserConceptsInput,
  ListUserConceptsQueryVariables,
  UserConcepts,
} from "../API";
import { listUserConceptsCustom, getUserConceptsCustom } from "../graphqlCustom/queries";
import {
  createUserConcepts,
  updateUserConcepts,
  deleteUserConcepts,
} from "../graphql/mutations";

const useResource = (listingName: string, singleName: string) => {
  const dispatch = useDispatch();
  const { showConfirm, showError } = useApp();

  async function fetch(params: any) {
    const { searchText, startIndex, limit, userID } = params;

    try {
      const filter: any = {
        deleted: { eq: "0" },
      };

      if (userID) {
        filter.createdByID = { eq: userID };
      }

      if (searchText && searchText.length > 0) {
        filter.defaultConcept = { contains: searchText.toLowerCase() };
      }

      const response: any = await API.graphql({
        query: listUserConceptsCustom,
        variables: {
          filter,
          limit: limit || 1000,
        },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });

      const listing = response.data.listUserConcepts.items || [];
      
      // Sort by createdAt descending (GraphQL doesn't support sort, so we do it client-side)
      listing.sort((a: any, b: any) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateB - dateA;
      });

      // Apply pagination client-side if needed
      const page = startIndex / limit;
      const paginatedListing = listing.slice(page * limit, (page + 1) * limit);

      return paginatedListing;
    } catch (err: Error | any) {
      console.log(err);
      showError(err?.message || "Error fetching user concepts");
    }
  }

  async function get(params: GetUserConceptsQueryVariables) {
    const { id } = params;

    try {
      const response: any = await API.graphql({
        query: getUserConceptsCustom,
        variables: { id },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });

      const single: UserConcepts | undefined = response.data.getUserConcepts;

      return single;
    } catch (err: Error | any) {
      console.log(err);
      showError(err?.message || "Error fetching user concept");
    }
  }

  async function create(params: any) {
    const { userID, userName, data } = params;

    const listingParams: any = {
      startIndex: 0,
      limit: 1000,
      searchText: "",
      userID: userID,
    };

    const existed = await fetch(listingParams);

    if (existed && existed.length > 0) {
      const updateParams: any = {
        id: existed[0].id,
        data: { concepts: data.concepts, conceptsRoles: data.conceptsRoles },
      };
      await update(updateParams);
      return existed[0];
    }

    try {
      const createInput: CreateUserConceptsInput = {
        defaultConcept: data.defaultConcept ? data.defaultConcept : "",
        concepts: data.concepts ? data.concepts : [],
        conceptsRoles: data.conceptsRoles ? data.conceptsRoles : [],
        deleted: "0",
        createdAt: new Date().toISOString(),
        createdByID: userID,
        createdByName: userName,
      };

      const response: any = await API.graphql({
        query: createUserConcepts,
        variables: { input: createInput },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });

      const model = response.data.createUserConcepts;

      return model;
    } catch (err: Error | any) {
      console.log(err);
      showError(err?.message || "Error creating user concept");
    }
  }

  async function update(params: any) {
    const { id, data } = params;

    try {
      const original = await get({ id });

      if (!original) {
        showError("User concept not found");
        return;
      }

      const updateInput: UpdateUserConceptsInput = {
        id: original.id,
        defaultConcept: data.defaultConcept
          ? data.defaultConcept
          : original.defaultConcept,
        concepts: data.concepts !== undefined ? data.concepts : original.concepts,
        conceptsRoles: data.conceptsRoles !== undefined ? data.conceptsRoles : original.conceptsRoles,
      };

      await API.graphql({
        query: updateUserConcepts,
        variables: { input: updateInput },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });

      showConfirm(`${singleName} has been updated successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err?.message || "Error updating user concept");
    }
  }

  async function trash(params: any) {
    try {
      const original = await get(params);

      if (!original) {
        showError("User concept not found");
        return;
      }

      const updateInput: UpdateUserConceptsInput = {
        id: original.id,
        deleted: "1",
      };

      await API.graphql({
        query: updateUserConcepts,
        variables: { input: updateInput },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });

      showConfirm(`${singleName} has been moved to trash successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err?.message || "Error moving user concept to trash");
    }
  }

  async function bulkTrash(params: any) {
    const { ids, listing } = params;

    ids.forEach(async (id: any) => {
      try {
        await trash(id);
      } catch (err: Error | any) {
        throw err;
      }
    });

    dispatch(setListing(listing.filter((model: any) => !ids.has(model.id))));

    showConfirm(`${ids.size} ${listingName} items has been moved to trash`);
  }

  async function remove(params: any) {
    const { id, listing } = params;

    try {
      await API.graphql({
        query: deleteUserConcepts,
        variables: { input: { id } },
        authMode: GRAPHQL_AUTH_MODE.AMAZON_COGNITO_USER_POOLS,
      });

      dispatch(setListing(listing.filter((model: any) => model.id !== id)));

      showConfirm(`${singleName} has been deleted successfully`);
    } catch (err: Error | any) {
      console.log(err);
      showError(err?.message || "Error deleting user concept");
    }
  }

  function options(listing: UserConcepts[]) {
    const options: any[] = [];

    for (let option of listing) {
      options.push({ label: option.id, value: option.id });
    }

    return options;
  }

  const headCells: readonly HeadCell[] = [
    {
      id: "name",
      numeric: false,
      disablePadding: false,
      label: "Name",
    },
    {
      id: "createdBy",
      numeric: false,
      disablePadding: false,
      label: "Created By",
    },
    {
      id: "createdAt",
      numeric: false,
      disablePadding: false,
      label: "Date",
    },
    {
      id: "actions",
      numeric: true,
      disablePadding: false,
      label: "",
    },
  ];

  const dataCells: readonly string[] = ["name"];

  const api: any = {};

  // Model removed - using GraphQL API types instead of DataStore models
  api[`${listingName}HeadCells`] = headCells;
  api[`${listingName}DataCells`] = dataCells;
  api[`${listingName}Options`] = options;
  api[`${listingName}Fetch`] = fetch;
  api[`${listingName}Get`] = get;
  api[`${listingName}Create`] = create;
  api[`${listingName}Update`] = update;
  api[`${listingName}Trash`] = trash;
  api[`${listingName}BulkTrash`] = bulkTrash;
  api[`${listingName}Delete`] = remove;
  api[`${listingName}ChangeListing`] = (listing: UserConcepts[]) =>
    dispatch(setListing(listing));
  api[`${listingName}ChangeSelected`] = (selected: UserConcepts | null) =>
    dispatch(setSelected(selected));

  return api;
};

export default useResource;